import React, { useCallback, useEffect } from 'react';
import moment from 'moment-timezone';
import { ColumnsType } from 'antd/es/table';
import AppCard from '@nxsys/components/AppCard';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTable from '@nxsys/components/AppTableContainer';
import AppIconButton from '@nxsys/components/AppIconButton';
import DocumentHeader from '../KIDHeader';
import DetailModalForCIS from './DetailModalForCIS';
import DetailModalForPaye from './DetailModalForPaye';
import DetailModalForUmbrella from './DetailModalForUmbrella';
import { ClipBoardIcon } from '@nxsys/components/icons';
import { useSelector } from 'react-redux';
import { AppState, AppDispatch, RootState } from '@nxsys/store';
import { useDispatch } from 'react-redux';
import { kidAction } from '@nxsys/store/slices/kid/kidSlice';
import { debounce } from 'lodash';
import { KIDEmailType, KIDPaymentModel } from '@nxsys/constants/AppConstants';
import { MailOutlined } from '@ant-design/icons';
import { showToast } from '@nxsys/utils/toast';
import { defaultTheme } from '@nxsys/constants/defaultConfig';

const EmailSent = () => {
  const { isAgency, isBureauEmployer, user } =
    useSelector((state: RootState) => state.appStore);
  const { kidHistories, visibleDetail, kidHistoryDetail } = useSelector(
    (state: AppState) => state.kid,
  );
  const agencyPortalId = isAgency ? user?.agencyId?.id : null;
  const companyUserIds = isBureauEmployer ? user?.companyUserIds: null;
  const {
    list,
    pagination: { pageSize, current, totalDocs  },
    queryParams
  } = kidHistories || {
    list: [],
    pagination: { totalDocs: 0, pageSize: 10, current: 1 },
  };

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(
      kidAction.getKidHistory({
        page: 0,
        size: 10,
      }),
    )
  }, []);

  const onToggleDetail = () => {
    dispatch(kidAction.hideKIDDetail());
  };

  const handleGetKIDHistories = (params: Record<string, string | number | undefined>) => {
    const newParams = {
      ...queryParams,
      ...params,
    };
    if (agencyPortalId) {
      newParams.agencyId = agencyPortalId;
    }
    if (companyUserIds?.length) {
      newParams.companyPaymentIds = companyUserIds;
    }
    dispatch(kidAction.getKidHistory(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    handleGetKIDHistories({
      page: totalDocs > pageSize ? page - 1 : 0,
      size: pageSize,
    });
  };
  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleGetKIDHistories({
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleGetKIDHistories({
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      handleGetKIDHistories({
        page: 0,
        size: 20,
        search: value,
      });
    }, 500),
    [],
  );

  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleResendEmail = async (emailLogId: string) => {
    try {
      await dispatch(kidAction.resendEmail({ emailLogId })).unwrap();
      showToast({ type: 'success', message: 'Email resent successfully!' });
      // Refetch the list
      handleGetKIDHistories({});
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to resent email';
      showToast({ type: 'error', message: errorMessage });
    }
  };

  const columns: ColumnsType<Record<string, string | unknown>> = [
    {
      key: 'actions',
      title: 'Document',
      fixed: 'left',
      align: 'left',
      width: 120,
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li>
            <AppIconButton
              title="View"
              icon={<ClipBoardIcon />}
              onClick={() => dispatch(kidAction.showKIDDetail(record))}
            />
          </li>
          <li>
            <AppIconButton
              title={
                record.status === 'SUCCESS'
                  ? 'Email Sent'
                  : record?.errorMessage || 'Email Not Sent'
              }
              icon={
                <MailOutlined
                  style={{
                    color:
                      record.status === 'SUCCESS'
                        ? defaultTheme.theme.palette.primary.main
                        : '#FF0000',
                  }}
                />
              }
              onClick={() => handleResendEmail(record.id)}
            />
          </li>
        </ul>
      ),
    },
    {
      key: 'subcontractorName',
      title: 'Name',
      fixed: 'left',
      dataIndex: 'subcontractorName',
    },
    {
      key: 'subcontractor',
      title: 'Company',
      dataIndex: 'subcontractor',
      render: (subcontractor) => subcontractor?.companyPayment?.companyName || '_',
    },
    {
      key: 'agency',
      title: 'Agency',
      dataIndex: 'agency',
      render: (agency) => agency?.name || '_',
    },
    {
      key: 'dataSend',
      title: 'Email ID',
      dataIndex: 'dataSend',
      width: 250,
      render: (dataSend) => dataSend?.mailTo || '_',
    },
    {
      key: 'action',
      title: 'Document Type',
      dataIndex: 'action',
      render: (value) =>
        KIDEmailType[value as keyof typeof KIDEmailType] || '_',
    },
    {
      key: 'action',
      title: 'Payment Model',
      dataIndex: 'action',
      render: (value) =>
        KIDPaymentModel[value as keyof typeof KIDPaymentModel] || '_',
    },
    {
      key: 'sentBy',
      title: 'Sent By',
      dataIndex: 'sentBy',
      align: 'left',
      render: (sentBy) => `${sentBy?.forename || ''} ${sentBy?.surname || ''}`,
    },
    {
      key: 'createdAt',
      title: 'Sent On',
      dataIndex: 'createdAt',
      render: (text) => moment(text).format('DD/MM/YYYY'),
    },
  ];

  const isFiltering = !!queryParams?.search;

  return (
    <AppCard>
      <AppsHeader>
        <DocumentHeader isCreate={false} onChangeSearch={onChangeSearchTerm} />
      </AppsHeader>
      <AppsContent>
        <AppTable
          total={totalDocs || 0}
          data={list || []}
          loading={false}
          page={current}
          pageSize={pageSize}
          columns={columns}
          titleEmpty={'There is no Email Sent'}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
          scroll={{ x: '100%', y: 700 }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      {(kidHistoryDetail?.action === 'SEND_KID' ||
        kidHistoryDetail?.action === 'SEND_KID_PAY_ILLUSTRATION') && (
        <DetailModalForCIS isOpen={visibleDetail} toggle={onToggleDetail} />
      )}
      {(kidHistoryDetail?.action === 'SEND_KID_PAYE' ||
        kidHistoryDetail?.action === 'SEND_KID_PAYE_PAY_ILLUSTRATION') && (
        <DetailModalForPaye isOpen={visibleDetail} toggle={onToggleDetail} />
      )}
      {(kidHistoryDetail?.action === 'SEND_KID_UMBRELLA' ||
        kidHistoryDetail?.action === 'SEND_KID_UMBRELLA_PAY_ILLUSTRATION') && (
        <DetailModalForUmbrella
          isOpen={visibleDetail}
          toggle={onToggleDetail}
        />
      )}
    </AppCard>
  );
};

export default EmailSent;
